import * as Dialog from '@wandb/weave/components/Dialog';
import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {type IconName} from '@wandb/weave/components/Icon';
import copyToClipboard from 'copy-to-clipboard';
import React, {useMemo} from 'react';

import {useSemanticLegendSettingsContext} from '../../../components/WorkspaceDrawer/Settings/semanticLegend/SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {type Viewer} from '../../../state/viewer/types';
import {type LoadableView} from '../../../state/views/types';
import {nwToast} from '../../../state/workspaces/namedWorkspaceToast';
import {
  type NamedWorkspaceId,
  NW_DEFAULT_ID,
} from '../../../state/workspaces/types';
import {useStatsigGatePersonalSavedViews} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import {useDetectPanelVariant, useGoToBaseUrl} from '../../Panel/hooks/hooks';
import {useIsWorkspaceTemplateActionDisplayed} from '../../WorkspaceTemplate/useIsWorkspaceTemplateEnabled';
import {WorkspaceTemplateDrawer} from '../../WorkspaceTemplate/WorkspaceTemplateDrawer';
import {
  useWorkspaceTemplateDrawerContext,
  WorkspaceTemplateDrawerContextProvider,
} from '../../WorkspaceTemplate/WorkspaceTemplateDrawerContextProvider';
import {
  useViewBarActionContext,
  useViewBarContext,
} from '../NamedWorkspaceViewBarContext';
import {
  getDisabledTooltipContent,
  getResetTooltipContent,
  parseAndTrackNamedWorkspaces,
} from '../utils';
import {makeOverflowMenuItem, OverflowMenu} from './OverflowMenu';
import {useSaveAndCopyActions} from './ViewBarButton';
import {WorkspaceTemplateOverflowMenuItem} from './WorkspaceTemplateOverflowMenuItem';

const ANONYMOUS_PROJECT_TOOLTIP_CONTENT =
  'Log in or create an account to perform workspace actions.';
const COPY_WORKSPACE_URL_TOOLTIP_CONTENT =
  'You can share personal workspaces and saved views with colleagues by copying their URL in the address bar.';

type EditAccessInfoArgs = {
  currentView: LoadableView | undefined;
  currentWorkspaceNwId: NamedWorkspaceId;
  viewer: Viewer | undefined;
  isPersonalSavedViewsEnabled: boolean;
  isEditAccessActionAvailable: boolean;
  shouldRevokeWritePerms: boolean;
};

export const getEditAccessInfo = ({
  currentView,
  currentWorkspaceNwId,
  viewer,
  isPersonalSavedViewsEnabled,
  isEditAccessActionAvailable,
  shouldRevokeWritePerms,
}: EditAccessInfoArgs) => {
  const isOwnedSavedView =
    currentWorkspaceNwId.nwType === 'shared workspace' &&
    currentWorkspaceNwId.id !== NW_DEFAULT_ID &&
    currentView != null &&
    viewer != null &&
    currentView.user.id === viewer.id;
  const editAccessAction = currentView?.locked
    ? ({
        displayName: 'Share with edit access',
        editScope: 'team',
        icon: 'lock-open',
      } as const)
    : ({
        displayName: 'Remove team edit access',
        editScope: 'owner',
        icon: 'lock-closed',
      } as const);

  return {
    ...editAccessAction,
    isDisplayed:
      isPersonalSavedViewsEnabled &&
      isOwnedSavedView &&
      (isEditAccessActionAvailable || shouldRevokeWritePerms),
    isDisabled:
      !isEditAccessActionAvailable ||
      shouldRevokeWritePerms ||
      currentView?.saving,
  };
};

type OverflowMenuItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenu.Item
> & {
  displayName: string;
  icon: IconName;
  onSelect: (e: Event) => void;
  tooltipContent?: React.ReactElement | string;
  children?: React.ReactNode;
};

export const NamedWorkspaceOverflowMenu = () => {
  const {viewer, currentWorkspaceType} = useViewBarContext();
  const isAnonymousProject = viewer == null;

  const {isWorkspaceTemplateActionDisplayed, isLoadingEntityType} =
    useIsWorkspaceTemplateActionDisplayed(currentWorkspaceType);

  return (
    <WorkspaceTemplateDrawerContextProvider
      isWorkspaceTemplateActionDisplayed={isWorkspaceTemplateActionDisplayed}>
      <OverflowMenu
        aria-label="Workspace actions"
        data-dd-action-name="More workspace actions"
        data-test="nw-overflow-menu-trigger"
        isDisabled={isAnonymousProject || isLoadingEntityType}
        variant="secondary"
        tooltip={
          isAnonymousProject ? ANONYMOUS_PROJECT_TOOLTIP_CONTENT : undefined
        }>
        <WorkspaceOverflowActions />
      </OverflowMenu>
      <WorkspaceTemplateDrawer />
    </WorkspaceTemplateDrawerContextProvider>
  );
};

const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  // TODO(react-compiler): see react/* suppressions. validateStaticComponents
  // does not catch this pattern, so the directive is still needed.
  'use no memo';
  const {isWorkspaceTemplateActionDisplayed} =
    useWorkspaceTemplateDrawerContext();
  const MenuItem = useMemo(
    () =>
      makeOverflowMenuItem({
        tooltipProps: {
          side: 'left',
          sideOffset: isWorkspaceTemplateActionDisplayed ? 280 : 205,
        },
      }),
    [isWorkspaceTemplateActionDisplayed]
  );
  // oxlint-disable-next-line react/static-components
  return <MenuItem {...props} />;
};

const ForwardedOverflowMenuItem = React.forwardRef<
  HTMLDivElement,
  OverflowMenuItemProps
>((props, ref) => {
  return <OverflowMenuItem {...props} />;
});
ForwardedOverflowMenuItem.displayName = 'ForwardedOverflowMenuItem';

const WorkspaceOverflowActions = () => {
  const {
    saveAsNewWorkspace,
    setWorkspaceEditScope,
    currentView,
    viewer,
    resetToDefaultSpec,
    isLoadingWorkspaceTemplates,
  } = useViewBarContext();
  const {semanticLegendSettings, semanticLegendRunColorOptions} =
    useSemanticLegendSettingsContext();
  const {setModalDetails} = useViewBarActionContext();
  const {copy, save} = useSaveAndCopyActions();
  const {
    editAccessInfo,
    saveAsNewWorkspaceInfo,
    resetToDefaultSpecInfo,
    deleteViewInfo,
  } = useOverflowMenuActions();

  const {isFullScreenPanelPage} = useDetectPanelVariant();
  const goToWorkspaceBaseUrl = useGoToBaseUrl();

  const shouldShowSeparator =
    resetToDefaultSpecInfo.isDisplayed || deleteViewInfo.isDisplayed;

  return (
    <>
      {save.isDisplayed && (
        <Dialog.Trigger asChild className="flex xl:hidden">
          <ForwardedOverflowMenuItem
            displayName="Save view"
            icon="save"
            disabled={save.isUiDisabled}
            data-dd-action-name="Save view"
            onSelect={() => save.fn(false)}
          />
        </Dialog.Trigger>
      )}
      <OverflowMenuItem
        displayName="Copy workspace URL"
        icon="copy"
        data-dd-action-name="Copy workspace link"
        onSelect={() => {
          parseAndTrackNamedWorkspaces({
            action: 'Copy workspace url',
            location: 'menu',
            currentView,
            viewer,
          });
          copyToClipboard(window.location.href);
          nwToast('Copied to clipboard');
        }}
        tooltipContent={COPY_WORKSPACE_URL_TOOLTIP_CONTENT}
      />
      {editAccessInfo.isDisplayed && (
        <OverflowMenuItem
          displayName={editAccessInfo.displayName}
          icon={editAccessInfo.icon}
          data-dd-action-name={editAccessInfo.displayName}
          disabled={editAccessInfo.isDisabled}
          onSelect={() => {
            parseAndTrackNamedWorkspaces({
              action: editAccessInfo.displayName,
              location: 'menu',
              currentView,
              viewer,
            });
            setWorkspaceEditScope?.(editAccessInfo.editScope);
          }}
          tooltipContent={
            editAccessInfo.isDisabled
              ? getDisabledTooltipContent(viewer)
              : undefined
          }
        />
      )}
      {saveAsNewWorkspaceInfo.isDisplayed && (
        <OverflowMenuItem
          displayName="Save as new view"
          icon="add-new"
          data-dd-action-name="Save as new view"
          disabled={saveAsNewWorkspaceInfo.isDisabled}
          onSelect={() => {
            parseAndTrackNamedWorkspaces({
              action: 'Save as new view',
              location: 'menu',
              currentView,
              viewer,
            });
            saveAsNewWorkspace?.('team', {
              ...semanticLegendSettings,
              runColorOptions: semanticLegendRunColorOptions,
            });
          }}
          tooltipContent={
            saveAsNewWorkspaceInfo.isDisabled
              ? getDisabledTooltipContent(viewer)
              : ''
          }
        />
      )}
      <WorkspaceTemplateOverflowMenuItem />
      {copy.isDisplayed && (
        <OverflowMenuItem
          className="flex xl:hidden"
          displayName="Copy to my workspace"
          icon="forward-next"
          data-dd-action-name="Copy to my workspace"
          onSelect={copy.fn}
        />
      )}
      {shouldShowSeparator && <DropdownMenu.Separator />}
      {resetToDefaultSpecInfo.isDisplayed && (
        <OverflowMenuItem
          disabled={resetToDefaultSpecInfo.isDisabled}
          displayName="Reset workspace"
          icon="randomize-reset-reload"
          data-dd-action-name="Reset workspace"
          onSelect={() => {
            parseAndTrackNamedWorkspaces({
              action: 'Reset workspace',
              location: 'menu',
              currentView,
              viewer,
            });
            resetToDefaultSpec?.();
            if (isFullScreenPanelPage) {
              goToWorkspaceBaseUrl();
            }
          }}
          tooltipContent={getResetTooltipContent({
            isLoadingWorkspaceTemplates,
            resetToDefaultSpecInfoIsDisabled: resetToDefaultSpecInfo.isDisabled,
            viewer,
          })}
        />
      )}
      {deleteViewInfo.isDisplayed && (
        <Dialog.Trigger asChild>
          <ForwardedOverflowMenuItem
            displayName="Delete view"
            icon="delete"
            disabled={deleteViewInfo.isDisabled}
            data-dd-action-name="Delete view"
            onSelect={() => {
              setModalDetails({
                type: 'delete view',
              });
            }}
            tooltipContent={
              deleteViewInfo.isDisabled
                ? getDisabledTooltipContent(viewer)
                : undefined
            }
          />
        </Dialog.Trigger>
      )}
    </>
  );
};

export const useOverflowMenuActions = () => {
  const {
    currentView,
    currentWorkspaceNwId,
    workspaceType,
    viewer,
    shouldRevokeWritePerms,
    resetToDefaultSpec,
    saveAsNewWorkspace,
    setWorkspaceEditScope,
    deleteViewAndLoad,
  } = useViewBarContext();
  const {isPersonalSavedViewsEnabled, isLoading} =
    useStatsigGatePersonalSavedViews(currentView?.project?.entityName);

  // if wb admin has powers on, saveAsNewWorkspace and deleteView should be displayed
  // but disabled to indicate they need to exit admin mode to make write changes.
  return {
    editAccessInfo: getEditAccessInfo({
      currentView,
      currentWorkspaceNwId,
      viewer,
      isPersonalSavedViewsEnabled: isPersonalSavedViewsEnabled && !isLoading,
      isEditAccessActionAvailable: setWorkspaceEditScope != null,
      shouldRevokeWritePerms,
    }),
    saveAsNewWorkspaceInfo: {
      isDisplayed: saveAsNewWorkspace != null || shouldRevokeWritePerms,
      isDisabled: shouldRevokeWritePerms || currentView?.saving,
    },
    resetToDefaultSpecInfo: {
      isDisplayed: resetToDefaultSpec != null,
      isDisabled: resetToDefaultSpec == null,
    },
    deleteViewInfo: {
      isDisplayed:
        workspaceType !== 'personal' &&
        (deleteViewAndLoad != null || shouldRevokeWritePerms),
      isDisabled: shouldRevokeWritePerms,
    },
  };
};
